
/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-06-29
 */

import React, { useState } from "react";
import { Container } from "react-bootstrap";
import { QueryListProvider } from "../../../../component/solution/QueryTableList/QueryListContext";
import { QueryTableList } from "../../../../component/solution/QueryTableList/QueryTableList";
import { IntegrationStatus } from "../../../../component/solution/Interfaces";
import { formatDateFull } from "../../../../component/solution/utils/AppUtil";



export const StockMaster: React.FC = () => {


    /* 

        Status 

        Stock Master - Activity Status

        TO_SEND - Item master ready to be sent to M24L.
        SENT - Item master has been sent to M24L for processing.
        ERROR - There was a problem integrating the item master at M24L.
        SUCCESS - Item master successfully received at M24L for processing.
    
    */ 


     /* 
      [Table Rows & Order To Display]: 
        item_code  
        item_group  
        name  
        barcode  
        article_code  
        gender  
        end_use  
        silhouette  
        sub_silhouette  
        minimum_quantity  
        maximum_quantity  
        integration_status  
        created  
        updated  
    */

    const getStatusColor = (status: string): { backgroundColor: string; color: string; } | {} => {
        if (IntegrationStatus[status as keyof typeof IntegrationStatus]) {
            return { backgroundColor: IntegrationStatus[status as keyof typeof IntegrationStatus], color: 'white' };
        }
        return {};
    };
 
    return(
        <React.Fragment>
            <Container fluid className="p-0">
            <QueryListProvider>
                    <QueryTableList
                        listTitle={"Stock Master - Activity Status"}
                        recordTitle={"Stock Master - Activity Status"}
                        tableName={"sa_m24l_item"}
                        editTableName={"sa_m24l_item"}
                        editHasCompoundKey={false}
                        hasIntegrationStatusLegend={true}
                        integrationstatusLegend={[
                            { status: 'ERROR', color: '#bf3636', description: 'There was a problem integrating the item master at M24L.'},
                            { status: 'CANCELLED', color: '#8b8b8c', description: 'Document has been cancelled.'},
                            { status: 'TO_SEND', color: '#d4a53f', description: 'Item master ready to be sent to M24L.'},
                            { status: 'SENT', color: '#d4a53f', description: 'Item master has been sent to M24L for processing.'},
                            { status: 'SUCCESS', color: '#3ec76b', description: 'Item master successfully received at M24L for processing.'}
                        ]}  
                        showFailedTransactionsTable={false}
                        query={{
                            appCode: null,
                            selectCode: null,
                            tableName: "sa_m24l_item",
                            where: "",
                            orderBy: "",
                            pageNum: 0,
                            pageSize: 100,
                            params: {},
                        }}   
                        orderColumns={[
                            { name: "item_code", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "item_group", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "name", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "barcode", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "article_code", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "gender", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "end_use", type: "INTEGER", sortable: true, searchable: true },
                            { name: "silhouette", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "sub_silhouette", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "minimum_quantity", type: "INTEGER", sortable: true, searchable: true },
                            { name: "maximum_quantity", type: "INTEGER", sortable: true, searchable: true },
                            { name: "integration_status", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "created", type: "TIMESTAMP", sortable: true, searchable: false},
                            { name: "updated", type: "TIMESTAMP", sortable: true, searchable: false}
                        ]}
                        reportOptions={{ 
                            reportQuery: {
                              appCode: null,
                              selectCode: null,
                              tableName: "sa_m24l_item", 
                              where: "",
                              orderBy: "",
                              pageNum: 0,
                              pageSize: 0,
                              params: {},
                            },
                            templateName: "apps/ares/item_master_template.xlsx", 
                            reportName: "item_master_report.xlsx",
                            reportData: null
                        }}   
                        renderRowData={(row) => {

                            const tdStyle: React.CSSProperties = { whiteSpace: 'nowrap', border: '1px solid #deebed',  padding: '4px 8px', fontSize: '0.75rem' };
                            const statusColor = getStatusColor(row.integration_status); 

                            return (
                                <>
                                    <td style={tdStyle} title={row.item_code}>{row.item_code}</td>
                                    <td style={tdStyle} title={row.item_group}>{row.item_group}</td>
                                    <td style={tdStyle} title={row.name}>{row.name}</td>
                                    <td style={tdStyle} title={row.barcode}>{row.barcode}</td>
                                    <td style={tdStyle} title={row.article_code}>{row.article_code}</td>
                                    <td style={tdStyle} title={row.gender}>{row.gender}</td>
                                    <td style={tdStyle} title={row.end_use}>{row.end_use}</td>
                                    <td style={tdStyle} title={row.silhouette}>{row.silhouette}</td>
                                    <td style={tdStyle} title={row.sub_silhouette}>{row.sub_silhouette}</td>
                                    <td style={tdStyle} title={row.minimum_quantity}>{row.minimum_quantity}</td>
                                    <td style={tdStyle} title={row.maximum_quantity}>{row.maximum_quantity}</td>
                                    <td style={{...tdStyle,...statusColor, textAlign: "center", verticalAlign: "middle"}} title={row.integration_status}>
                                        {row.integration_status}
                                    </td>
                                    <td style={tdStyle} title={formatDateFull(String(row.created))}>{formatDateFull(String(row.created))}</td>
                                    <td style={tdStyle} title={formatDateFull(String(row.updated))}>{formatDateFull(String(row.updated))}</td>
                                </>    
                            );
                        }}
                        renderActions={(row) => {
                            return (
                                 <td className="sa-record-control" style={{ border: '1px solid #deebed', height: '45px', fontSize: '0.55rem', padding: '0 5px'}}>
                                    
                                 </td>   
                            );
                        }}

                    />
                </QueryListProvider>
            </Container>
        </React.Fragment>
    );
}